import { Injectable } from '@angular/core';
import { GanttSegment } from '../components/util/ara-bar-chart/gantt.models';

interface EventPoint {
	date: Date;
	type: 'start' | 'end';
	segment: GanttSegment;
}

@Injectable({
	providedIn: 'root',
})
export class GanttOverlapService {
	public calculateOverlapSegments(segments: GanttSegment[]): GanttSegment[] {
		if (segments.length < 2) {
			return [];
		}

		// 1. Create Event Points for all segment start and end dates.
		const eventPoints: EventPoint[] = segments.flatMap((segment) => [
			{ date: segment.from, type: 'start', segment },
			{ date: segment.to, type: 'end', segment },
		]);

		// 2. Sort the event points chronologically.
		// If two points have the same date, process 'start' events before 'end' events.
		eventPoints.sort((a, b) => {
			const dateComparison = a.date.getTime() - b.date.getTime();
			if (dateComparison !== 0) {
				return dateComparison;
			}
			// 'start' comes before 'end'
			return a.type === 'start' ? -1 : 1;
		});

		const overlaps: GanttSegment[] = [];
		const activeSegments = new Set<GanttSegment>();

		// 3. Sweep through the event points.
		for (let i = 0; i < eventPoints.length - 1; i++) {
			const currentPoint = eventPoints[i];
			const nextPoint = eventPoints[i + 1];

			// Update the set of active segments based on the current point.
			if (currentPoint.type === 'start') {
				activeSegments.add(currentPoint.segment);
			} else {
				activeSegments.delete(currentPoint.segment);
			}

			// 4. Analyze the interval BETWEEN the current and next point.
			const intervalStart = currentPoint.date;
			const intervalEnd = nextPoint.date;

			// If there are 2 or more active segments and the interval has a duration, it's an overlap.
			if (activeSegments.size > 1 && intervalEnd.getTime() > intervalStart.getTime()) {
				overlaps.push({
					from: intervalStart,
					to: intervalEnd,
					value: 'Overlap',
					color: 'url(#overlap-pattern)',
					isOverlap: true,
					// The underlying segments are all the segments currently in the active set.
					underlyingSegments: Array.from(activeSegments),
				});
			}
		}

		return overlaps;
	}
}
